const desktopMedia = window.matchMedia("(min-width: 768px)");
const initializedAccordions = new WeakSet();
const initializedScrollCarousels = new WeakSet();
const initializedSection4QuickBuys = new WeakSet();
const initializedVideoShowcases = new WeakSet();
const initializedStoryLists = new WeakSet();
const warmedQuickBuyUrls = new Set();
const svgNamespace = "http://www.w3.org/2000/svg";
const reducedMotionMedia = window.matchMedia("(prefers-reduced-motion: reduce)");

function createGuideLine(section) {
  section
    .querySelectorAll(":scope > .wd030-guide-line")
    .forEach((existingGuide) => existingGuide.remove());

  const svg = document.createElementNS(svgNamespace, "svg");
  const path = document.createElementNS(svgNamespace, "path");
  const endpoint = document.createElementNS(svgNamespace, "circle");

  svg.classList.add("wd030-guide-line");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("preserveAspectRatio", "none");
  endpoint.setAttribute("r", "4");
  svg.append(path, endpoint);
  section.append(svg);

  return { svg, path, endpoint };
}

function initializeAccordion(section) {
  const stage = section.querySelector(':scope > [handle="container1"]');
  const accordion = section.querySelector(':scope > [handle="container2"]');

  if (!accordion || initializedAccordions.has(accordion)) return;

  const panels = stage
    ? Array.from(stage.children).filter(
        (child) => child.getAttribute("handle") === "container",
      )
    : [];
  const items = Array.from(accordion.children).filter(
    (child) => child.getAttribute("handle") === "container",
  );

  if (!items.length) return;

  const guide = createGuideLine(section);
  let activeIndex = -1;
  let guideFrame = 0;
  let guideTimer = 0;
  let guideShouldReveal = false;
  let guideShouldAnimate = false;

  const cancelGuideAnimation = () => {
    guide.path.getAnimations().forEach((animation) => animation.cancel());
    guide.endpoint.getAnimations().forEach((animation) => animation.cancel());
  };

  const drawGuideLine = () => {
    cancelGuideAnimation();

    if (reducedMotionMedia.matches) {
      guide.path.style.strokeDasharray = "none";
      guide.path.style.strokeDashoffset = "0";
      guide.endpoint.style.opacity = "1";
      return;
    }

    const pathLength = guide.path.getTotalLength();

    guide.path.style.strokeDasharray = `${pathLength}`;
    guide.path.style.strokeDashoffset = `${pathLength}`;
    guide.endpoint.style.opacity = "0";
    guide.path.animate(
      [
        { strokeDashoffset: pathLength },
        { strokeDashoffset: 0 },
      ],
      {
        duration: 480,
        delay: 140,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "forwards",
      },
    );
    guide.endpoint.animate(
      [
        { opacity: 0 },
        { opacity: 1 },
      ],
      { duration: 140, easing: "ease-out", fill: "forwards" },
    );
  };

  const updateGuideLine = () => {
    guideFrame = 0;

    if (!desktopMedia.matches || !panels[activeIndex] || !items[activeIndex]) {
      guide.svg.classList.remove("is-visible");
      return;
    }

    const leftCopy = panels[activeIndex].querySelector('[handle="richtext"]');
    const rightLabel = items[activeIndex].querySelector('[handle="text"]');

    if (!leftCopy || !rightLabel) {
      guide.svg.classList.remove("is-visible");
      return;
    }

    const sectionRect = section.getBoundingClientRect();
    const leftCopyRect = leftCopy.getBoundingClientRect();
    const itemRect = items[activeIndex].getBoundingClientRect();
    const rightLabelRect = rightLabel.getBoundingClientRect();
    const copyGap = Math.max(
      24,
      Math.min(48, sectionRect.width * 0.025),
    );
    const cardGap = Math.max(
      12,
      Math.min(24, sectionRect.width * 0.0125),
    );
    const leftEndX = leftCopyRect.right - sectionRect.left + copyGap;
    const leftEndY = leftCopyRect.bottom - sectionRect.top;
    const rightStartX = itemRect.left - sectionRect.left - cardGap;
    const rightStartY =
      rightLabelRect.top + rightLabelRect.height / 2 - sectionRect.top;
    const horizontalSpace = rightStartX - leftEndX;

    if (
      sectionRect.width <= 0 ||
      sectionRect.height <= 0 ||
      horizontalSpace < 48
    ) {
      guide.svg.classList.remove("is-visible");
      return;
    }

    const bendX = leftEndX + horizontalSpace * 0.55;

    guide.svg.setAttribute(
      "viewBox",
      `0 0 ${sectionRect.width} ${sectionRect.height}`,
    );
    guide.path.setAttribute(
      "d",
      `M ${rightStartX} ${rightStartY} L ${bendX} ${leftEndY} H ${leftEndX}`,
    );
    guide.endpoint.setAttribute("cx", String(rightStartX));
    guide.endpoint.setAttribute("cy", String(rightStartY));
    guide.svg.classList.toggle("is-visible", guideShouldReveal);

    if (guideShouldReveal && guideShouldAnimate) {
      guideShouldAnimate = false;
      drawGuideLine();
    }
  };

  const scheduleGuideUpdate = () => {
    if (guideFrame) return;
    guideFrame = window.requestAnimationFrame(updateGuideLine);
  };

  const scheduleSettledGuideUpdate = () => {
    window.clearTimeout(guideTimer);
    guideTimer = window.setTimeout(() => {
      guideShouldReveal = true;
      guideShouldAnimate = true;
      scheduleGuideUpdate();
    }, 240);
  };

  const updateCardGeometry = () => {
    if (activeIndex < 0) return;

    const accordionStyle = window.getComputedStyle(accordion);
    const parsedGap = Number.parseFloat(accordionStyle.rowGap);
    const gap = Number.isFinite(parsedGap) ? parsedGap : 0;
    const availableHeight = accordion.clientHeight - gap * (items.length - 1);
    const collapsedHeight = Math.max(
      0,
      availableHeight / (items.length + 3),
    );
    const expandedHeight = collapsedHeight * 4;
    const collapsedClip = (expandedHeight - collapsedHeight) / 2;
    let slotY = 0;

    items.forEach((item, index) => {
      const isActive = index === activeIndex;
      const clip = isActive ? 0 : collapsedClip;

      item.style.setProperty(
        "--wd030-card-height",
        `${expandedHeight.toFixed(3)}px`,
      );
      item.style.setProperty(
        "--wd030-card-clip",
        `${clip.toFixed(3)}px`,
      );
      item.style.setProperty(
        "--wd030-card-y",
        `${(slotY - clip).toFixed(3)}px`,
      );

      slotY += (isActive ? expandedHeight : collapsedHeight) + gap;
    });
  };

  const setActiveItem = (nextIndex) => {
    const normalizedIndex = Math.min(nextIndex, items.length - 1);

    if (normalizedIndex === activeIndex) return;

    activeIndex = normalizedIndex;
    guideShouldReveal = false;
    guideShouldAnimate = false;
    cancelGuideAnimation();
    guide.svg.classList.remove("is-visible");
    updateCardGeometry();

    items.forEach((item, index) => {
      const isActive = index === activeIndex;
      item.classList.toggle("is-active", isActive);
    });

    panels.forEach((panel, index) => {
      const isActive = index === activeIndex;
      panel.classList.toggle("is-active", isActive);
      panel.setAttribute("aria-hidden", String(!isActive));
    });

    scheduleGuideUpdate();
    scheduleSettledGuideUpdate();
  };

  const syncResponsiveState = () => {
    if (desktopMedia.matches) {
      stage?.classList.toggle("is-enhanced", panels.length > 0);
      accordion.classList.add("is-enhanced", "is-positioning");
      setActiveItem(0);
      window.requestAnimationFrame(() => {
        accordion.classList.remove("is-positioning");
      });
      return;
    }

    items.forEach((item) => {
      item.classList.remove("is-active");
    });
    panels.forEach((panel) => {
      panel.classList.remove("is-active");
      panel.removeAttribute("aria-hidden");
    });
    stage?.classList.remove("is-enhanced");
    accordion.classList.remove("is-enhanced", "is-positioning");
    window.clearTimeout(guideTimer);
    guide.svg.classList.remove("is-visible");
    guideShouldReveal = false;
    guideShouldAnimate = false;
    cancelGuideAnimation();
    activeIndex = -1;
  };

  items.forEach((item, index) => {
    item.addEventListener("mouseenter", () => {
      if (desktopMedia.matches) setActiveItem(index);
    });
  });

  accordion.addEventListener("transitionend", (event) => {
    if (
      event.propertyName !== "transform" ||
      event.target !== items[activeIndex]
    ) {
      return;
    }

    window.clearTimeout(guideTimer);
    guideShouldReveal = true;
    guideShouldAnimate = true;
    scheduleGuideUpdate();
  });

  const resizeObserver = window.ResizeObserver
    ? new window.ResizeObserver(() => {
        if (desktopMedia.matches && activeIndex >= 0) {
          accordion.classList.add("is-positioning");
          updateCardGeometry();
          window.requestAnimationFrame(() => {
            accordion.classList.remove("is-positioning");
          });
        }

        scheduleGuideUpdate();
      })
    : null;

  [section, stage, accordion].filter(Boolean).forEach((element) => {
    resizeObserver?.observe(element);
  });
  if (!resizeObserver) {
    window.addEventListener("resize", scheduleGuideUpdate, { passive: true });
  }
  document.fonts?.ready.then(scheduleGuideUpdate);

  desktopMedia.addEventListener("change", syncResponsiveState);
  syncResponsiveState();
  initializedAccordions.add(accordion);
}

function initializeSection3(root = document) {
  const sections = root.matches?.('[handle="section3"]')
    ? [root]
    : root.querySelectorAll('[handle="section3"]');

  sections.forEach((section) => {
    initializeAccordion(section);
    initializeScrollCarousel(section);
  });
}

function initializeVerticalScrollCarousel(section) {
  const embla = section.querySelector(':scope > [handle="embla"][data-embla]');
  const viewport = embla?.querySelector("[data-embla-viewport]");
  const track = embla?.querySelector("[data-embla-container]");
  const slides = track
    ? Array.from(track.children).filter((child) =>
        child.hasAttribute("data-embla-slide"),
      )
    : [];
  if (
    !embla ||
    !viewport ||
    slides.length < 2 ||
    initializedScrollCarousels.has(section)
  ) {
    return;
  }

  let emblaApi = null;
  let scrollFrame = 0;
  let layoutFrame = 0;
  let pinFrame = 0;
  let scrollStep = 1;
  let stickyTop = -1;
  let viewportHeight = -1;
  let sectionHeight = -1;
  let activeIndex = 0;
  let touchStartY = 0;
  let dots = [];
  let touchStartedAtTop = false;
  let touchStartedAtBottom = false;
  let pageTouchLastY = 0;
  let pageTouchActive = false;
  let pageTouchStartedPinned = false;
  let touchHandoffEnabled = false;
  let touchHandoffActive = false;
  let touchHandoffDistance = 0;
  let touchHandoffStartIndex = 0;
  let lastPageScrollY = window.scrollY;
  let pinnedPageScrollY = window.scrollY;
  let pinnedPageResetTimer = 0;
  let isPinned = false;
  let pinPlaceholder = null;
  const controller = new AbortController();
  embla.dataset.emblaDisableDrag = "true";

  const isAtBottom = () =>
    viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop <= 1;

  const getNaturalSectionHeight = () => {
    const ratioValue = window
      .getComputedStyle(slides[0])
      .getPropertyValue("--aspect-ratio-mb")
      .trim();
    const [ratioWidth, ratioHeight] = ratioValue
      .split("/")
      .map((value) => Number.parseFloat(value));
    if (!(ratioWidth > 0) || !(ratioHeight > 0)) {
      return Number.POSITIVE_INFINITY;
    }

    const sectionWidth = window.innerWidth;
    return (sectionWidth * ratioHeight) / ratioWidth;
  };

  const syncStickyTop = (forceHeight = false) => {
    const headerBottom = document
      .querySelector("#pageheader")
      ?.getBoundingClientRect().bottom;
    const fallback = Number.isFinite(headerBottom)
      ? 0
      : Number.parseFloat(
          window
            .getComputedStyle(document.documentElement)
            .getPropertyValue("--theme-sticky-header-height"),
        ) || 0;
    const nextViewportHeight =
      window.visualViewport?.height || window.innerHeight;
    const nextStickyTop = Math.min(
      nextViewportHeight,
      Math.max(0, Number.isFinite(headerBottom) ? headerBottom : fallback),
    );
    const nextSectionHeight = Math.max(
      1,
      Math.min(nextViewportHeight - nextStickyTop, getNaturalSectionHeight()),
    );
    const shouldSyncHeight =
      (!isPinned || forceHeight) &&
      Math.abs(nextSectionHeight - sectionHeight) >= 0.5;
    if (
      Math.abs(nextStickyTop - stickyTop) < 0.5 &&
      Math.abs(nextViewportHeight - viewportHeight) < 0.5 &&
      !shouldSyncHeight
    ) {
      return false;
    }

    stickyTop = nextStickyTop;
    viewportHeight = nextViewportHeight;
    section.style.setProperty(
      "--wd030-section3-sticky-top",
      `${nextStickyTop}px`,
    );
    if (shouldSyncHeight) {
      sectionHeight = nextSectionHeight;
      section.style.setProperty(
        "--wd030-section3-height",
        `${nextSectionHeight}px`,
      );
    }
    return true;
  };

  const refreshInteractiveLayout = () => {
    if (!syncStickyTop(true)) return;

    if (pinPlaceholder) {
      pinPlaceholder.style.height = `${section.getBoundingClientRect().height}px`;
    }
    scrollStep = Math.max(
      1,
      viewport.clientHeight || embla.getBoundingClientRect().height,
    );
    viewport.scrollTo({
      top: activeIndex * scrollStep,
      behavior: "auto",
    });
    scheduleScrollSync();
  };

  const cancelPinnedPageReset = () => {
    window.clearTimeout(pinnedPageResetTimer);
    pinnedPageResetTimer = 0;
  };

  const resetPinnedPagePosition = () => {
    cancelPinnedPageReset();
    if (!isPinned || desktopMedia.matches || !section.isConnected) return;
    if (Math.abs(window.scrollY - pinnedPageScrollY) <= 1) return;

    window.scrollTo({ top: pinnedPageScrollY, behavior: "auto" });
    lastPageScrollY = pinnedPageScrollY;
  };

  const schedulePinnedPageReset = () => {
    cancelPinnedPageReset();
    pinnedPageResetTimer = window.setTimeout(resetPinnedPagePosition, 120);
  };

  function transferTouchToViewport(event) {
    if (!pageTouchActive) return;

    const currentY = event.touches[0]?.clientY ?? pageTouchLastY;
    const deltaY = pageTouchLastY - currentY;
    pageTouchLastY = currentY;
    if (!isPinned || pageTouchStartedPinned || !deltaY) return;

    if (!touchHandoffActive) {
      touchHandoffActive = true;
      touchHandoffDistance = 0;
      touchHandoffStartIndex = Math.round(viewport.scrollTop / scrollStep);
      viewport.classList.add("is-touch-handoff");
    }

    if (event.cancelable) event.preventDefault();
    touchHandoffDistance += deltaY;
    viewport.scrollTop = Math.max(
      0,
      Math.min(
        viewport.scrollHeight - viewport.clientHeight,
        viewport.scrollTop + deltaY,
      ),
    );
    scheduleScrollSync();
  }

  const enableTouchHandoff = () => {
    if (touchHandoffEnabled) return;

    touchHandoffEnabled = true;
    window.addEventListener("touchmove", transferTouchToViewport, {
      passive: false,
      signal: controller.signal,
    });
  };

  const disableTouchHandoff = () => {
    if (!touchHandoffEnabled) return;

    window.removeEventListener("touchmove", transferTouchToViewport);
    touchHandoffEnabled = false;
  };

  const beginPageTouch = (event) => {
    pageTouchActive = true;
    pageTouchStartedPinned = isPinned;
    pageTouchLastY = event.touches[0]?.clientY ?? 0;
    touchHandoffActive = false;
    touchHandoffDistance = 0;

    if (desktopMedia.matches || isPinned) return;
    const bounds = section.getBoundingClientRect();
    const currentViewportHeight =
      window.visualViewport?.height || window.innerHeight;
    const isApproachingSection =
      bounds.top <= currentViewportHeight * 2 &&
      bounds.bottom >= -currentViewportHeight;
    if (isApproachingSection) enableTouchHandoff();
  };

  const finishPageTouch = () => {
    if (touchHandoffActive && isPinned) {
      const movedToNextSlide = Math.abs(touchHandoffDistance) >= 12;
      const nextIndex = movedToNextSlide
        ? touchHandoffStartIndex + Math.sign(touchHandoffDistance)
        : Math.round(viewport.scrollTop / scrollStep);
      const targetIndex = Math.max(
        0,
        Math.min(slides.length - 1, nextIndex),
      );

      viewport.classList.remove("is-touch-handoff");
      viewport.scrollTo({
        top: targetIndex * scrollStep,
        behavior: reducedMotionMedia.matches ? "auto" : "smooth",
      });
      setActiveSlide(targetIndex);
    } else {
      viewport.classList.remove("is-touch-handoff");
    }

    pageTouchActive = false;
    touchHandoffActive = false;
    touchHandoffDistance = 0;
    disableTouchHandoff();
  };

  const setPinned = (shouldPin) => {
    const nextPinned = shouldPin && !desktopMedia.matches;
    if (nextPinned === isPinned) return;

    if (nextPinned) {
      pinPlaceholder = document.createElement("div");
      pinPlaceholder.className = "wd030-section3-pin-placeholder";
      pinPlaceholder.setAttribute("aria-hidden", "true");
      pinPlaceholder.style.height = `${section.getBoundingClientRect().height}px`;
      section.before(pinPlaceholder);
      section.classList.add("is-pinned-scroll-carousel");
      pinnedPageScrollY = window.scrollY;
      isPinned = true;
      if (pageTouchActive && !pageTouchStartedPinned) enableTouchHandoff();
      return;
    }

    section.classList.remove("is-pinned-scroll-carousel");
    pinPlaceholder?.remove();
    pinPlaceholder = null;
    cancelPinnedPageReset();
    isPinned = false;
    lastPageScrollY = window.scrollY;
    sectionHeight = -1;
    if (!desktopMedia.matches) syncStickyTop();
  };

  const beginTouch = (event) => {
    refreshInteractiveLayout();
    touchStartY = event.touches[0]?.clientY ?? 0;
    touchStartedAtTop = viewport.scrollTop <= 1;
    touchStartedAtBottom = isAtBottom();
  };

  const updateTouchChain = (event) => {
    if (!isPinned) return;

    const currentY = event.touches[0]?.clientY ?? touchStartY;
    const delta = currentY - touchStartY;
    const shouldReleaseToPage =
      (touchStartedAtTop && viewport.scrollTop <= 1 && delta > 4) ||
      (touchStartedAtBottom && isAtBottom() && delta < -4);
    if (!shouldReleaseToPage) return;

    setPinned(false);
  };

  const releaseWheelToPage = (event) => {
    if (!isPinned) return;

    const shouldReleaseToPage =
      (event.deltaY < 0 && viewport.scrollTop <= 1) ||
      (event.deltaY > 0 && isAtBottom());
    if (!shouldReleaseToPage) return;

    setPinned(false);
    window.scrollBy({
      top: event.deltaY,
      behavior: "auto",
    });
  };

  const setActiveSlide = (index) => {
    activeIndex = Math.max(0, Math.min(slides.length - 1, index));
    slides.forEach((slide, slideIndex) => {
      slide.setAttribute("aria-hidden", String(slideIndex !== activeIndex));
    });
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === activeIndex;
      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-current", isActive ? "true" : "false");
    });
  };

  const mountDots = () => {
    const dotsRoot = embla.querySelector("[data-embla-dots]");
    if (!dotsRoot) return;
    if (
      dots.length === slides.length &&
      dots.every((dot) => dot.parentElement === dotsRoot)
    ) {
      setActiveSlide(activeIndex);
      return;
    }

    dots = slides.map((_, index) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "wd030-section3-scroll-dot";
      dot.setAttribute("aria-label", `View feature ${index + 1}`);
      dot.addEventListener("click", () => {
        refreshInteractiveLayout();
        viewport.scrollTo({
          top: index * scrollStep,
          behavior: reducedMotionMedia.matches ? "auto" : "smooth",
        });
      });
      return dot;
    });
    dotsRoot.replaceChildren(...dots);
    setActiveSlide(activeIndex);
  };

  const syncToViewportScroll = () => {
    scrollFrame = 0;
    if (desktopMedia.matches || !section.isConnected) return;
    const nextIndex = Math.round(viewport.scrollTop / scrollStep);
    if (nextIndex !== activeIndex) setActiveSlide(nextIndex);
  };

  const scheduleScrollSync = () => {
    if (!scrollFrame) {
      scrollFrame = window.requestAnimationFrame(syncToViewportScroll);
    }
  };

  const syncSlideLayout = () => {
    layoutFrame = 0;
    if (desktopMedia.matches || !section.isConnected) return;

    if (pinPlaceholder) {
      pinPlaceholder.style.height = `${section.getBoundingClientRect().height}px`;
    }
    scrollStep = Math.max(
      1,
      viewport.clientHeight || embla.getBoundingClientRect().height,
    );
    viewport.scrollTo({
      top: activeIndex * scrollStep,
      behavior: "auto",
    });
    scheduleScrollSync();
  };

  const scheduleSlideLayout = () => {
    if (!layoutFrame) {
      layoutFrame = window.requestAnimationFrame(syncSlideLayout);
    }
  };

  const syncPinnedPosition = () => {
    pinFrame = 0;
    const currentPageScrollY = window.scrollY;
    if (desktopMedia.matches || !section.isConnected) {
      setPinned(false);
      lastPageScrollY = currentPageScrollY;
      return;
    }

    if (isPinned) {
      if (Math.abs(currentPageScrollY - pinnedPageScrollY) > 1) {
        schedulePinnedPageReset();
      }
      lastPageScrollY = currentPageScrollY;
      return;
    }

    if (syncStickyTop()) scheduleSlideLayout();

    const sectionBounds = section.getBoundingClientRect();
    const direction = currentPageScrollY - lastPageScrollY;
    const canScrollInside =
      direction < 0 ? viewport.scrollTop > 1 : !isAtBottom();
    const reachedStickyTop =
      sectionBounds.top <= stickyTop + 1 &&
      sectionBounds.bottom > stickyTop + 1;
    const visibleHeight = Math.max(
      0,
      Math.min(sectionBounds.bottom, viewportHeight) -
        Math.max(sectionBounds.top, stickyTop),
    );
    const effectiveSectionHeight = Math.max(
      1,
      Math.min(sectionBounds.height, viewportHeight - stickyTop),
    );
    const reachedUpwardThreshold =
      direction >= 0 || visibleHeight > effectiveSectionHeight * 0.5;

    if (canScrollInside && reachedStickyTop && reachedUpwardThreshold) {
      const correction = sectionBounds.top - stickyTop;
      if (Math.abs(correction) > 1) {
        window.scrollBy({ top: correction, behavior: "auto" });
      }
      setPinned(true);
    }
    lastPageScrollY = window.scrollY;
  };

  const schedulePinSync = () => {
    if (!pinFrame) {
      pinFrame = window.requestAnimationFrame(syncPinnedPosition);
    }
  };

  const syncLayout = () => {
    if (desktopMedia.matches) {
      setPinned(false);
      section.classList.remove("is-scroll-carousel");
      section.style.removeProperty("--wd030-section3-sticky-top");
      section.style.removeProperty("--wd030-section3-height");
      stickyTop = -1;
      viewportHeight = -1;
      sectionHeight = -1;
      slides.forEach((slide) => slide.removeAttribute("aria-hidden"));
      return;
    }

    syncStickyTop();
    section.classList.add("is-scroll-carousel");
    connectEmbla();
    if (pinPlaceholder) {
      pinPlaceholder.style.height = `${section.getBoundingClientRect().height}px`;
    }
    scheduleSlideLayout();
    schedulePinSync();
  };

  const handleViewportResize = () => {
    if (isPinned) return;
    syncLayout();
  };

  const connectEmbla = () => {
    if (desktopMedia.matches) return;

    const api = embla.__tideweEmblaApi;
    if (api && api !== emblaApi) {
      emblaApi = api;
      api.destroy?.();

      const instances = window.theme?.EmblasInstances;
      if (Array.isArray(instances)) {
        const index = instances.indexOf(api);
        if (index >= 0) instances.splice(index, 1);
      }
      delete embla.__tideweEmblaApi;
    }

    mountDots();
    scheduleScrollSync();
  };

  const observer = new MutationObserver(connectEmbla);
  observer.observe(embla, {
    attributes: true,
    attributeFilter: ["data-embla-initialized"],
  });
  connectEmbla();

  const cleanup = (event) => {
    if (!event.target.contains(section)) return;
    controller.abort();
    observer.disconnect();
    window.cancelAnimationFrame(scrollFrame);
    window.cancelAnimationFrame(layoutFrame);
    window.cancelAnimationFrame(pinFrame);
    cancelPinnedPageReset();
    finishPageTouch();
    setPinned(false);
    section.style.removeProperty("--wd030-section3-sticky-top");
    section.style.removeProperty("--wd030-section3-height");
    desktopMedia.removeEventListener("change", syncLayout);
    initializedScrollCarousels.delete(section);
  };

  viewport.addEventListener("scroll", scheduleScrollSync, {
    passive: true,
    signal: controller.signal,
  });
  viewport.addEventListener("touchstart", beginTouch, {
    passive: true,
    signal: controller.signal,
  });
  viewport.addEventListener("touchmove", updateTouchChain, {
    passive: true,
    signal: controller.signal,
  });
  viewport.addEventListener("wheel", releaseWheelToPage, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("touchstart", beginPageTouch, {
    passive: false,
    signal: controller.signal,
  });
  window.addEventListener("touchend", finishPageTouch, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("touchcancel", finishPageTouch, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("scroll", schedulePinSync, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("scrollend", resetPinnedPagePosition, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("resize", handleViewportResize, {
    passive: true,
    signal: controller.signal,
  });
  window.visualViewport?.addEventListener("resize", handleViewportResize, {
    passive: true,
    signal: controller.signal,
  });
  document.addEventListener("shopify:section:unload", cleanup, {
    signal: controller.signal,
  });
  desktopMedia.addEventListener("change", syncLayout);
  syncLayout();
  initializedScrollCarousels.add(section);
}

function initializeScrollCarousel(section) {
  if (section.getAttribute("handle") === "section3") {
    initializeVerticalScrollCarousel(section);
    return;
  }

  const embla = section.querySelector(':scope > [handle="embla"][data-embla]');
  const track = embla?.querySelector("[data-embla-container]");
  const slides = track
    ? Array.from(track.children).filter((child) =>
        child.hasAttribute("data-embla-slide"),
      )
    : [];
  if (!embla || slides.length < 2 || initializedScrollCarousels.has(section)) {
    return;
  }

  let emblaApi = null;
  let scrollFrame = 0;
  let scrollStep = 1;
  const controller = new AbortController();

  const getNaturalSlideHeight = () => {
    const ratioValue = window
      .getComputedStyle(slides[0])
      .getPropertyValue("--aspect-ratio-mb")
      .trim();
    const [ratioWidth, ratioHeight] = ratioValue
      .split("/")
      .map((value) => Number.parseFloat(value));
    if (!(ratioWidth > 0) || !(ratioHeight > 0)) {
      return Number.POSITIVE_INFINITY;
    }

    const sectionWidth =
      section.getBoundingClientRect().width || window.innerWidth;
    return (sectionWidth * ratioHeight) / ratioWidth;
  };

  const syncSlideMetrics = () => {
    const headerBottom = document
      .querySelector("#pageheader")
      ?.getBoundingClientRect().bottom;
    const fallback =
      Number.parseFloat(
        window
          .getComputedStyle(document.documentElement)
          .getPropertyValue("--theme-sticky-header-height"),
      ) || 0;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const stickyTop = Math.min(
      viewportHeight,
      Math.max(0, Number.isFinite(headerBottom) ? headerBottom : fallback),
    );
    const availableHeight = viewportHeight - stickyTop;
    const nextScrollStep = Math.max(
      1,
      Math.min(availableHeight, getNaturalSlideHeight()),
    );

    section.style.setProperty(
      "--wd030-section4-sticky-top",
      `${stickyTop}px`,
    );
    section.style.setProperty(
      "--wd030-section4-slide-height",
      `${nextScrollStep}px`,
    );
    return nextScrollStep;
  };

  const syncToPageScroll = () => {
    scrollFrame = 0;
    if (desktopMedia.matches || !emblaApi || !section.isConnected) return;

    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    const traveled = Math.max(0, window.scrollY - sectionTop);
    const index = Math.min(slides.length - 1, Math.floor(traveled / scrollStep));
    if (emblaApi.selectedSnap() !== index) {
      emblaApi.goTo(index, reducedMotionMedia.matches);
    }
  };

  const scheduleScrollSync = () => {
    if (!scrollFrame) {
      scrollFrame = window.requestAnimationFrame(syncToPageScroll);
    }
  };

  const syncLayout = () => {
    if (desktopMedia.matches) {
      section.classList.remove("is-scroll-carousel");
      section.style.removeProperty("--wd030-scroll-carousel-height");
      section.style.removeProperty("--wd030-section4-sticky-top");
      section.style.removeProperty("--wd030-section4-slide-height");
      return;
    }

    section.classList.add("is-scroll-carousel");
    scrollStep = syncSlideMetrics();
    section.style.setProperty(
      "--wd030-scroll-carousel-height",
      `${(slides.length + 1) * scrollStep}px`,
    );
    scheduleScrollSync();
  };

  const connectEmbla = () => {
    const api = embla.__tideweEmblaApi;
    if (!api || api === emblaApi) return;
    emblaApi = api;
    scheduleScrollSync();
  };

  const observer = new MutationObserver(connectEmbla);
  observer.observe(embla, {
    attributes: true,
    attributeFilter: ["data-embla-initialized"],
  });
  connectEmbla();

  const cleanup = (event) => {
    if (!event.target.contains(section)) return;
    controller.abort();
    observer.disconnect();
    window.cancelAnimationFrame(scrollFrame);
    desktopMedia.removeEventListener("change", syncLayout);
    initializedScrollCarousels.delete(section);
  };

  window.addEventListener("scroll", scheduleScrollSync, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("resize", syncLayout, {
    passive: true,
    signal: controller.signal,
  });
  window.visualViewport?.addEventListener("resize", syncLayout, {
    passive: true,
    signal: controller.signal,
  });
  document.addEventListener("shopify:section:unload", cleanup, {
    signal: controller.signal,
  });
  desktopMedia.addEventListener("change", syncLayout);
  syncLayout();
  initializedScrollCarousels.add(section);
}

function initializeSection4(root = document) {
  const sections = root.matches?.('[handle="section4"]')
    ? [root]
    : root.querySelectorAll('[handle="section4"]');

  sections.forEach((section) => {
    if (initializedSection4QuickBuys.has(section)) return;

    const buttons = Array.from(
      section.querySelectorAll(
        ':scope > [handle="container"] [data-product-quickbuy="true"]',
      ),
    );
    if (!buttons.length) return;

    buttons.forEach((button) => {
      const productUrl = button.getAttribute("data-product-href");
      if (productUrl && !button.hasAttribute("data-href")) {
        button.setAttribute("data-href", productUrl);
      }
    });

    let primeAttempts = 0;
    const primeMobileQuickBuy = () => {
      if (desktopMedia.matches || !section.isConnected) return;

      const handlerReady = document.getElementById("quickbuy-handler-root");
      const themeReady =
        typeof window.theme?.globalMobileQuickbuyModal === "function";
      if ((!handlerReady || !themeReady) && primeAttempts < 120) {
        primeAttempts += 1;
        window.setTimeout(primeMobileQuickBuy, 50);
        return;
      }

      const bootstrap = document.createElement("button");
      bootstrap.type = "button";
      bootstrap.hidden = true;
      bootstrap.setAttribute("data-product-quickbuy", "true");
      document.body.append(bootstrap);
      bootstrap.click();
      bootstrap.remove();

      if (
        !document.getElementById("product-button-mobile-container") &&
        primeAttempts < 120
      ) {
        primeAttempts += 1;
        window.setTimeout(primeMobileQuickBuy, 50);
      }
    };

    primeMobileQuickBuy();

    const warmProductPages = () => {
      buttons.forEach((button) => {
        const productUrl = button.getAttribute("data-product-href");
        if (!productUrl || warmedQuickBuyUrls.has(productUrl)) return;

        warmedQuickBuyUrls.add(productUrl);
        fetch(productUrl, {
          credentials: "same-origin",
          priority: "low",
        }).catch(() => {
          warmedQuickBuyUrls.delete(productUrl);
        });
      });
    };

    if (typeof IntersectionObserver === "function") {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          warmProductPages();
        },
        { rootMargin: "1500px 0px" },
      );
      observer.observe(section);
    } else {
      warmProductPages();
    }

    initializedSection4QuickBuys.add(section);
  });
}

function initializeVideoShowcase(section) {
  const track = Array.from(section.children).find(
    (child) =>
      child.getAttribute("handle") === "container" &&
      child.querySelector(':scope > [handle="container-video"]'),
  );

  if (!track || initializedVideoShowcases.has(track)) return;

  const cards = Array.from(track.children).filter(
    (child) => child.getAttribute("handle") === "container-video",
  );

  if (!cards.length) return;

  section
    .querySelectorAll(":scope > .wd030-video-dots")
    .forEach((existingDots) => existingDots.remove());

  const dots = document.createElement("div");
  dots.className = "wd030-video-dots";
  dots.setAttribute("role", "tablist");
  dots.setAttribute("aria-label", "Video slides");
  const dotButtons = cards.map((card, index) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "wd030-video-dot";
    button.setAttribute("role", "tab");
    button.setAttribute("aria-label", `Show video ${index + 1}`);
    dots.append(button);
    return button;
  });
  section.append(dots);

  const autoplayDelay = 5000;
  let activeIndex = 0;
  let autoplayTimer = 0;
  let scrollFrame = 0;
  let scrollEndTimer = 0;
  let pointerIsDown = false;

  const clearAutoplay = () => {
    window.clearTimeout(autoplayTimer);
    autoplayTimer = 0;
  };

  const syncCards = () => {
    const isDesktop = desktopMedia.matches;

    track.classList.toggle("is-video-enhanced", isDesktop);
    track.classList.toggle("is-mobile-carousel", !isDesktop);
    dots.hidden = isDesktop;
    dotButtons.forEach((button, index) => {
      const isActive = index === activeIndex;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-selected", String(isActive));
      button.tabIndex = isActive ? 0 : -1;
    });
    cards.forEach((card, index) => {
      const isActive = index === activeIndex;
      const video = card.querySelector(":scope > .container-bg-video");

      card.classList.toggle("is-active", isActive);
      if (isDesktop) {
        card.removeAttribute("aria-hidden");
      } else {
        card.setAttribute("aria-hidden", String(!isActive));
      }

      if (!video) return;

      if (isActive) {
        try {
          const playPromise = video.play();
          playPromise?.catch(() => {});
        } catch {}
      } else {
        video.pause();
      }
    });
  };

  const getCardScrollLeft = (card) => {
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    return track.scrollLeft + cardRect.left - trackRect.left;
  };

  const scrollToCard = (nextIndex, behavior = "smooth") => {
    activeIndex = (nextIndex + cards.length) % cards.length;
    syncCards();
    track.scrollTo({
      left: getCardScrollLeft(cards[activeIndex]),
      behavior,
    });
  };

  const scheduleAutoplay = () => {
    clearAutoplay();

    if (
      desktopMedia.matches ||
      cards.length < 2 ||
      document.hidden ||
      reducedMotionMedia.matches
    ) {
      return;
    }

    autoplayTimer = window.setTimeout(() => {
      scrollToCard(activeIndex + 1);
      scheduleAutoplay();
    }, autoplayDelay);
  };

  const syncActiveCardFromScroll = () => {
    scrollFrame = 0;
    if (desktopMedia.matches) return;

    const trackLeft = track.getBoundingClientRect().left;
    let nearestIndex = activeIndex;
    let nearestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
      if (distance >= nearestDistance) return;

      nearestDistance = distance;
      nearestIndex = index;
    });

    if (nearestIndex === activeIndex) return;

    activeIndex = nearestIndex;
    syncCards();
  };

  const scheduleScrollSync = () => {
    if (!scrollFrame) {
      scrollFrame = window.requestAnimationFrame(syncActiveCardFromScroll);
    }

    window.clearTimeout(scrollEndTimer);
    scrollEndTimer = window.setTimeout(() => {
      syncActiveCardFromScroll();
      if (!pointerIsDown) scheduleAutoplay();
    }, 180);
  };

  const syncResponsiveState = () => {
    clearAutoplay();
    window.clearTimeout(scrollEndTimer);

    if (desktopMedia.matches) {
      track.scrollLeft = 0;
      syncCards();
      return;
    }

    syncCards();
    window.requestAnimationFrame(() => {
      scrollToCard(activeIndex, "auto");
    });
    scheduleAutoplay();
  };

  cards.forEach((card, index) => {
    card.addEventListener("mouseenter", () => {
      if (!desktopMedia.matches || index === activeIndex) return;

      activeIndex = index;
      syncCards();
    });
  });

  dotButtons.forEach((button, index) => {
    button.addEventListener("click", () => {
      if (desktopMedia.matches) return;
      clearAutoplay();
      scrollToCard(index);
      scheduleAutoplay();
    });
  });

  track.addEventListener("scroll", scheduleScrollSync, { passive: true });
  track.addEventListener(
    "pointerdown",
    () => {
      pointerIsDown = true;
      clearAutoplay();
    },
    { passive: true },
  );
  ["pointerup", "pointercancel"].forEach((eventName) => {
    track.addEventListener(
      eventName,
      () => {
        pointerIsDown = false;
        syncActiveCardFromScroll();
        scheduleAutoplay();
      },
      { passive: true },
    );
  });
  track.addEventListener("focusin", clearAutoplay);
  track.addEventListener("focusout", scheduleAutoplay);
  document.addEventListener("visibilitychange", scheduleAutoplay);
  reducedMotionMedia.addEventListener("change", scheduleAutoplay);

  const videoObserver = new MutationObserver(syncResponsiveState);
  videoObserver.observe(track, { childList: true, subtree: true });
  desktopMedia.addEventListener("change", syncResponsiveState);

  syncResponsiveState();
  initializedVideoShowcases.add(track);
}

function initializeSection5(root = document) {
  const sections = root.matches?.('[handle="section5"]')
    ? [root]
    : root.querySelectorAll('[handle="section5"]');

  sections.forEach(initializeVideoShowcase);
}

function initializeStoryList(section) {
  const directContainers = Array.from(section.children).filter(
    (child) => child.getAttribute("handle") === "container",
  );
  const list =
    section.querySelector(':scope > [handle="container2"]') ||
    directContainers.find((container) =>
      container.querySelector(':scope > [handle="container"]'),
    );

  if (!list || initializedStoryLists.has(list)) return;

  const existingTrack = list.querySelector(":scope > .wd030-story-track");
  const cardParent = existingTrack || list;
  const cards = Array.from(cardParent.children).filter(
    (child) =>
      child.getAttribute("handle") === "container" &&
      child.dataset.wd030StoryClone !== "true",
  );

  if (!cards.length) return;

  const scrollSpeed = 80;
  const hoverScrollSpeed = 36;
  let track = existingTrack;
  let cloneCards = [];
  let trackAnimation = null;
  let activeStateTimer = 0;
  let resizeFrame = 0;
  let lastListWidth = 0;
  let activeCard = null;
  let isPointerInside = false;

  const getTrackCards = () => [...cards, ...cloneCards];

  const mountTrack = () => {
    if (track) {
      track
        .querySelectorAll('[data-wd030-story-clone="true"]')
        .forEach((clone) => clone.remove());
      cards.forEach((card) => track.append(card));
    } else {
      track = document.createElement("div");
      track.className = "wd030-story-track";
      list.insertBefore(track, cards[0]);
      cards.forEach((card) => track.append(card));
    }
    track.style.removeProperty("transform");

    cloneCards = cards.map((card) => {
      const clone = card.cloneNode(true);
      clone.removeAttribute("id");
      clone
        .querySelectorAll("[id]")
        .forEach((child) => child.removeAttribute("id"));
      clone.dataset.wd030StoryClone = "true";
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("inert", "");
      track.append(clone);
      return clone;
    });
  };

  const setActiveCard = (nextCard) => {
    const nextIndex = nextCard
      ? getTrackCards().indexOf(nextCard) % cards.length
      : -1;
    const nextOriginal = nextIndex >= 0 ? cards[nextIndex] : null;

    if (activeCard === nextOriginal) return;

    getTrackCards().forEach((card, index) => {
      card.classList.toggle(
        "is-active",
        nextIndex >= 0 && index % cards.length === nextIndex,
      );
    });
    activeCard = nextOriginal;
  };

  const stopCarousel = () => {
    trackAnimation?.cancel();
    trackAnimation = null;
    window.clearInterval(activeStateTimer);
    activeStateTimer = 0;
    list.classList.remove("is-story-rotating");
  };

  const setCarouselSpeed = (speed) => {
    if (!trackAnimation) return;

    const rate = speed / scrollSpeed;
    if (typeof trackAnimation.updatePlaybackRate === "function") {
      trackAnimation.updatePlaybackRate(rate);
    } else {
      trackAnimation.playbackRate = rate;
    }
  };

  const syncActiveCard = () => {
    if (!track) return;

    const listBounds = list.getBoundingClientRect();
    const cardBounds = getTrackCards().map((card) => ({
      card,
      bounds: card.getBoundingClientRect(),
    }));
    const nextCard =
      cardBounds.find(
        ({ bounds }) =>
          bounds.top >= listBounds.top - 0.5 &&
          bounds.bottom <= listBounds.bottom + 0.5,
      )?.card ||
      cardBounds.find(
        ({ bounds }) =>
          bounds.top >= listBounds.top && bounds.top < listBounds.bottom,
      )?.card ||
      cardBounds.find(
        ({ bounds }) =>
          bounds.bottom > listBounds.top && bounds.top < listBounds.bottom,
      )?.card;

    setActiveCard(nextCard || null);
  };

  const startCarousel = (preserveProgress = false) => {
    const previousDuration = trackAnimation?.effect?.getTiming().duration;
    const previousTime = trackAnimation?.currentTime;
    const progress =
      preserveProgress && previousDuration && previousTime !== null
        ? (previousTime % previousDuration) / previousDuration
        : 0;
    stopCarousel();

    if (
      reducedMotionMedia.matches ||
      !track ||
      cards.length < 2 ||
      typeof track.animate !== "function"
    ) {
      syncActiveCard();
      return;
    }

    const cycleDistance =
      cloneCards[0].getBoundingClientRect().top -
      cards[0].getBoundingClientRect().top;
    if (!Number.isFinite(cycleDistance) || cycleDistance <= 0) return;

    const duration = (cycleDistance / scrollSpeed) * 1000;
    list.classList.add("is-story-rotating");
    trackAnimation = track.animate(
      [
        { transform: "translate3d(0, 0, 0)" },
        { transform: `translate3d(0, -${cycleDistance}px, 0)` },
      ],
      {
        duration,
        easing: "linear",
        iterations: Infinity,
      },
    );
    if (progress) trackAnimation.currentTime = progress * duration;
    setCarouselSpeed(isPointerInside ? hoverScrollSpeed : scrollSpeed);
    lastListWidth = list.getBoundingClientRect().width;
    syncActiveCard();
    activeStateTimer = window.setInterval(syncActiveCard, 100);
  };

  const syncResponsiveState = () => {
    stopCarousel();
    mountTrack();
    list.classList.add("wd030-story-list", "is-story-enhanced");
    setActiveCard(cards[0]);
    startCarousel();
  };

  list.addEventListener("pointerenter", () => {
    isPointerInside = true;
    setCarouselSpeed(hoverScrollSpeed);
  });
  list.addEventListener("pointerleave", () => {
    isPointerInside = false;
    setCarouselSpeed(scrollSpeed);
  });

  reducedMotionMedia.addEventListener("change", syncResponsiveState);
  desktopMedia.addEventListener("change", syncResponsiveState);
  window.addEventListener(
    "resize",
    () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0;
        if (Math.abs(list.getBoundingClientRect().width - lastListWidth) > 0.5) {
          startCarousel(true);
        }
      });
    },
    { passive: true },
  );

  syncResponsiveState();
  initializedStoryLists.add(list);
}

function initializeSection6(root = document) {
  const sections = root.matches?.('[handle="section6"]')
    ? [root]
    : root.querySelectorAll('[handle="section6"]');

  sections.forEach(initializeStoryList);
}

function initializePage(root = document) {
  initializeSection3(root);
  initializeSection4(root);
  initializeSection5(root);
  initializeSection6(root);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializePage(), {
    once: true,
  });
} else {
  initializePage();
}

document.addEventListener("shopify:section:load", (event) => {
  initializePage(event.target);
});
